import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import NoCommentsIcon from '@mui/icons-material/ModeCommentOutlined';
import CommentsIcon from '@mui/icons-material/ModeComment';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import { Theme, Grid, Typography } from '@mui/material';
import { getStyleVariables } from 'styles/vars';
import { Difference, DifferenceTypeNames, DocumentTypes } from 'types';
import { inspection, getSelectedCustomDictionaryIDs, getInspectionId, getHighlightColor, app } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { Lens } from '@mui/icons-material';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import GVIconButton from 'components/lib/GVIconButton/GVIconButton';
import { useMutation } from '@redux-requests/react';
import {
  restoreAllDifferences,
  updateDifference,
  unmergeDifference,
  deleteMarkup,
  unlinkEmbeddedImageMatch,
} from 'store/request/differences/actions';
import { patchGroup } from 'store/request/difference-group/actions';
import DifferenceComments from './DifferenceComments';
import DifferenceDescription from './DifferenceDescription/DifferenceDescription';
import GroupChip from './GroupChip';
import GraphicDifference from './differenceTypes/GraphicDifference';
import MergedGraphicDifference from './differenceTypes/MergedGraphicDifference';
import UnmatchedAnnotationDifference from './differenceTypes/UnmatchedAnnotationDifference';
import InsertionDifference from './differenceTypes/InsertionDifference';
import SpellingDifference from './differenceTypes/SpellingDifference';
import DeletionDifference from './differenceTypes/DeletionDifference';
import BarcodeDifference from './differenceTypes/BarcodeDifference';
import { makeStyles } from 'tss-react/mui';
import BrailleDifference from './differenceTypes/BrailleDifference';
import ColorDifference from './differenceTypes/ColorDifference';
import MarkupDifference from './differenceTypes/MarkupDifference';
import ReadingOrderDifference from './differenceTypes/ReadingOrderDifference';
import { useSuggestedWords } from 'store/queries/dictionaries/suggestedWords';
import SmartDiscardIcon from 'components/icons/SmartDiscardIcon/SmartDiscardIcon';
import AccessAlarmsIcon from '@mui/icons-material/AccessAlarms';
import { debounce } from 'lodash';
import CustomStatusButton from './CustomStatusButton';
import ContextTag from './ContextTag';
import { useTracker } from 'components/Tracker/TrackerProvider';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  DEFAULT_DIFFERENCE_COLOR,
  differenceHighlightColors,
  DifferenceTypesWithHighlightColor,
} from 'pdftron/docManager/differenceColors';
import { ColorId } from '@verify/shared-utils';
import MultipleSelectionCheckbox from './MultipleSelectionCheckbox';
import store from 'store/store';
import DeleteIcon from '@mui/icons-material/Delete';
import MergeIcon from '@mui/icons-material/MergeType';
import UndoIcon from '@mui/icons-material/Undo';
import ResyncIcon from 'components/icons/Resync/ResyncIcon';
import DifferenceUtils from 'pdftron/docManager/Difference';
import LinkedImageIcon from 'components/icons/EmbeddedImages/LinkedImageIcon';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { FALSE_POSITIVE_COMMENT, isFalsePositiveComment } from './constants';
import styles from './DifferenceElementView.module.css';

interface DifferenceElementViewProps {
  elementRef: React.ForwardedRef<HTMLDivElement>;
  numberOfDifference: number;
  groupIndex?: number;
  selectDifference: () => void;
  isSelected: boolean;
  isCommentOpen: boolean;
  handleToggleComment: (
    e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | KeyboardEvent,
    open: boolean,
  ) => void;
  difference: Difference;
  groupLength: number;
  differenceNumber?: number;
  isKeyboardFocused: boolean;
  nextDiffId: string;
  handleOnSaveComment: (comment: string) => void;
  handleOnStopKeyboardNavigation: (index: number, behavior?: ScrollBehavior) => void;
  multipleSelectionMode?: boolean;
  isCheckboxSelected?: boolean;
  onCheckboxChange?: (difference: Difference, checked: boolean) => void;
  customStatuses?: any;
  canSuggestToCustomDictionary?: boolean;
  refetchStatuses?: () => void;
}

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    root: {
      borderTop: `1px solid rgba(0,0,0,0)`,
      borderLeft: `3px solid rgba(0,0,0,0)`,
      padding: theme.spacing(1.5, 2, 1.5, 3),
      margin: theme.spacing(0, 1),
      width: 'calc(100% - 16px)',
      borderRadius: '4px',
      position: 'relative', // This allows absolute positioning of child elements
      transition: 'background-color 300ms ease-out',
      '--gds-size-field-textarea-height': 'auto',
      '&:hover $number': {
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: theme.palette.primary.main,
      },
      '&:hover': {
        backgroundColor: styleVariables.global.hoverBackground,
        // When hovering the entire card, make the checkbox background match
        '& $checkboxContainer > div': {
          backgroundColor: styleVariables.global.hoverBackground,
        },
      },
    },

    backgroundColorEnabled: {
      backgroundColor: styleVariables.global.enabledBackground,
    },
    backgroundColorRead: {
      backgroundColor: styleVariables.global.readBackground,
    },
    backgroundColorActive: {
      backgroundColor: styleVariables.global.hoverBackground,
    },
    selectedMultiple: {
      backgroundColor: styleVariables.global.enabledBackground,
      '&:hover': {
        backgroundColor: styleVariables.global.hoverBackground,
      },
    },
    selectedMultipleHover: {
      backgroundColor: styleVariables.global.hoverBackground,
    },
    multipleSelectionUnselected: {
      backgroundColor: styleVariables.global.enabledBackground,
    },
    keyboardFocused: {
      backgroundColor: '#47484b',
    },
    annotationSelected: {
      borderLeft: `3px solid ${styleVariables.colors.lightOrange}`,
      backgroundColor: theme.palette.action.disabled,
    },
    title: {
      flex: 1,
      marginBottom: theme.spacing(2),
    },
    number: {
      padding: theme.spacing(0.25, 0.5),
      borderRadius: '4px',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    numberSelected: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      backgroundColor: theme.palette.primary.main,
    },
    icons: {
      maxWidth: theme.spacing(2),
      maxHeight: theme.spacing(2),
      margin: theme.spacing(0.25),
    },
    rightMargin: {
      marginRight: theme.spacing(1),
    },
    iconContainer: {
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(0.5),
      width: '100%',
      maxWidth: '100%',
      overflow: 'visible',
      paddingRight: 0,
      transition: 'padding-left 300ms ease-out',
      position: 'relative',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxSizing: 'border-box',
    },
    customActionsButtons: {
      gap: theme.spacing(0.5),
      minHeight: '32px',
      display: 'flex',
      alignItems: 'center',
      transition: 'padding-left 300ms ease-out',
    },
    rightActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      flexWrap: 'nowrap',
      overflow: 'visible',
      pointerEvents: 'auto',
      maxWidth: '120px',
      minWidth: '120px',
    },
    // Holds the range label and the context tag on one row so a card can show both.
    cardMeta: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(3),
      zIndex: 1,
      pointerEvents: 'auto',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    rangeLabel: {
      fontSize: '12px',
      color: 'rgba(255, 255, 255, 0.4)',
      pointerEvents: 'none',
      userSelect: 'none',
      whiteSpace: 'nowrap',
    },
    differenceType: {
      fontWeight: 500,
      color: '#FFFFFF99',
    },
    disabledButton: {
      '&.Mui-disabled': {
        pointerEvents: 'auto',
      },
    },
    indicator: {
      fontSize: theme.spacing(1),
    },
    indicatorGrid: {
      position: 'absolute',
      left: theme.spacing(1),
      top: theme.spacing(1.7),
      transition: 'left 300ms ease-out',
    },
    indicatorWithCheckbox: {
      left: theme.spacing(2),
    },
    indicatorUnread: {
      color: styleVariables.global.viewedIndicator,
    },
    indicatorRead: {
      visibility: 'hidden',
    },
    loading: {
      color: 'rgba(255,255,255,0.38)',
    },
    spinnerContainer: {
      padding: theme.spacing(0.625),
    },
    loadingOpacity: {
      opacity: 0.38,
    },
    textContainer: {
      // TEMP: used for annotationDifference
      paddingBottom: theme.spacing(1),
    },
    // TEMP: used for annotationDifference and graphic
    text: {
      color: 'var(--gds-color-text-supportive)',
    },
    success: {
      color: theme.palette.success.main,
    },
    restoreButton: {
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
      },
    },
    restoreButtonColor: {
      color: '#2ED2AB',
      cursor: 'pointer',
    },
    suggestWordIcon: {
      cursor: 'pointer',
      fontSize: '18px',
    },
    falsePositiveIcon: {
      cursor: 'pointer',
    },
    smartDiscardIcon: {
      marginLeft: theme.spacing(1),
      verticalAlign: 'middle',
      fontSize: '18px',
    },
    smartDiscardTooltipPopper: {
      '& .MuiTooltip-tooltip': {
        maxWidth: '220px',
      },
    },
    accessAlarm: {
      color: '#ff9800',
      fontSize: '18px',
      cursor: 'default',
    },

    checkboxContainer: {
      position: 'absolute',
      left: '9px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1,
      width: '24px',
      height: '24px',
      borderRadius: '50%',
    },
    transition: {
      transition: 'padding-left 300ms ease-out',
    },
    commentContainer: {
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      marginRight: 0,
      marginLeft: 0,
      paddingRight: 0,
      paddingLeft: 0,
      boxSizing: 'border-box',
    },
    multipleSelectionChecked: {
      backgroundColor: '#02A4B626',
      transition: 'background-color 300ms ease-out',
    },
    hover: {
      '&:hover': {
        backgroundColor: styleVariables.global.hoverBackground,
      },
    },
    checkedHover: {
      '&:hover': {
        backgroundColor: '#02A4B64D',
      },
    },
  };
});

export interface DifferenceProps {
  difference: Difference;
  isSelected: boolean;
  classes: Omit<ReturnType<typeof useStyles>, 'theme' | 'css' | 'cx'>;
  differenceNumber?: number;
  multipleSelectionMode?: boolean;
}
const descriptionComponents: Partial<Record<DifferenceTypeNames, React.FC<DifferenceProps>>> = {
  [DifferenceTypeNames.SpellingGeneral]: SpellingDifference,
  [DifferenceTypeNames.Abbreviation]: SpellingDifference,
  [DifferenceTypeNames.ProperNoun]: SpellingDifference,
  [DifferenceTypeNames.Alphanumeric]: SpellingDifference,
  [DifferenceTypeNames.Insertion]: InsertionDifference,
  [DifferenceTypeNames.Deletion]: DeletionDifference,
  [DifferenceTypeNames.UnmatchedAnnotation]: UnmatchedAnnotationDifference,
  [DifferenceTypeNames.EmbeddedImage]: GraphicDifference,
  [DifferenceTypeNames.Graphics]: GraphicDifference,
  [DifferenceTypeNames.Barcode]: BarcodeDifference,
  [DifferenceTypeNames.Braille]: BrailleDifference,
  [DifferenceTypeNames.Color]: ColorDifference,
  [DifferenceTypeNames.Markup]: MarkupDifference,
  [DifferenceTypeNames.ReadingOrder]: ReadingOrderDifference,
};

/* difference description types currently being conditionally changed
  1. spelling // DONE
  2. insertion // DONE
  3. deletion // DONE
  3. graphic // TODO
  3. annotation // DONE
   */

const DifferenceElementView = (props: DifferenceElementViewProps) => {
  const { customStatuses, canSuggestToCustomDictionary = false, refetchStatuses } = props;
  const {
    elementRef,
    numberOfDifference,
    groupIndex,
    selectDifference,
    isSelected,
    difference,
    handleToggleComment,
    handleOnSaveComment,
    isCommentOpen,
    isKeyboardFocused,
    multipleSelectionMode,
    isCheckboxSelected,
    onCheckboxChange,
  } = props;
  const tracker = useTracker();
  const { classes, cx } = useStyles();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hotkeyLockRef = useRef<boolean>(false);
  const customDictionaryId = useSelector(getSelectedCustomDictionaryIDs)[0];
  const inspectionId = useSelector(getInspectionId);
  const dispatch = useDispatch();
  const flags = useFlags();
  const falsePositive = isFalsePositiveComment(difference?.comment);
  const highlightColor = useSelector(getHighlightColor);

  const { suggestWordMutation, suggestedWordsList } = useSuggestedWords();
  const isSpellingDiff =
    difference.type === DifferenceTypeNames.SpellingGeneral ||
    difference.type === DifferenceTypeNames.Abbreviation ||
    difference.type === DifferenceTypeNames.ProperNoun ||
    difference.type === DifferenceTypeNames.Alphanumeric;
  const isEmbeddedImage = difference.type === DifferenceTypeNames.EmbeddedImage;
  const isUnmatchedAnnotation = difference.type === DifferenceTypeNames.UnmatchedAnnotation;
  const isIntendedStatus = difference.intendedChangeStatus;
  const isFontMetrics = difference.type === DifferenceTypeNames.FontMetrics;
  const isMarkup = difference.type === DifferenceTypeNames.Markup;
  const isResynced = difference.resyncId != null;

  // Helper function to detect merged differences
  const isMergedDifference = (diff: Difference): boolean => {
    // Check if this difference has originalDifferenceIds (indicating it's a merged difference)
    return !!(diff.originalDifferenceIds && diff.originalDifferenceIds.length > 0);
  };
  const isMerged = isMergedDifference(difference);
  const isUserLinkedEmbeddedImage = isEmbeddedImage && !!difference.embeddedImageDetails?.userLinkedMatch;
  const isActionable = isMarkup || isMerged || isUserLinkedEmbeddedImage;

  const useContextTag =
    isEmbeddedImage || isUnmatchedAnnotation || isIntendedStatus || isFontMetrics || isResynced || isActionable;
  const confidenceLevel = difference.embeddedImageDetails?.matchDetails[0]?.confidenceLevel || 'None';

  const hasBeenSuggested = useMemo(() => {
    return (
      isSpellingDiff &&
      suggestedWordsList.find((suggestedWord) => {
        return (
          (suggestedWord.suggestedWord === difference.source.text &&
            suggestedWord.suggestedDictionaryId === customDictionaryId) ||
          (suggestedWord.suggestedWord === difference.source.text && !customDictionaryId)
        );
      })
    );
  }, [difference.type, difference.source.text, suggestedWordsList]);

  const statusHotkeyMap = {
    F1: 'First',
    F2: 'Second',
    F3: 'Third',
  };

  const suggestedWordTooltip = !canSuggestToCustomDictionary
    ? "Cannot suggest words to another organization's dictionary"
    : hasBeenSuggested
      ? 'Awaiting approval'
      : 'Add to dictionary';

  const { loading: loadingRestoreAllDifferences, error: loadingRestoreAllError } = useMutation({
    type: restoreAllDifferences,
  });
  const { loading: differenceLoading, error: updateDifferenceError } = useMutation({
    type: updateDifference,
    requestKey: difference.id,
  });
  const { loading: groupLoading, error: patchGroupError } = useMutation({
    type: patchGroup,
    requestKey: difference.groupId,
  });

  // Moved to parent component for performance - passed as props
  // const { data: statuses, refetchStatuses } = useInspectionCustomStatuses(inspectionId);
  const statuses = customStatuses;

  const handleSelectStatus = async (statusId: string | null) => {
    const updates = { statusId };
    if (difference.groupId) {
      await dispatch(patchGroup(inspectionId, difference.groupId, updates));
    } else {
      await dispatch(updateDifference(difference.id, updates));
    }
    refetchStatuses?.();
  };

  const handleRemoveMarkup = () => {
    dispatch(deleteMarkup(difference.id));
  };

  const handleUnmerge = () => {
    const inspectionId = getInspectionId(store.getState());
    if (!inspectionId) {
      dispatch(
        app.actions.setSnackMessage({
          message: 'Unable to unmerge: inspection not found',
          type: 'error',
        }),
      );
      return;
    }

    dispatch(unmergeDifference(difference.id, inspectionId));
  };

  const handleUnlinkEmbeddedImage = () => {
    dispatch(unlinkEmbeddedImageMatch(difference.id));
  };

  const calculateTagProperties = () => {
    const confidenceLevels = {
      None: { color: 'Grey', label: 'DEVIATION DETECTED' },
      Low: { color: 'Red', label: 'MAJOR CHANGES' },
      Medium: { color: 'Orange', label: 'SOME CHANGES' },
      High: { color: 'Green', label: 'LIKELY MATCH' },
    } as const;
    const getConfidenceLevel = (confidenceLevel: 'None' | 'Low' | 'Medium' | 'High') =>
      confidenceLevels[confidenceLevel];

    const getTooltipText = (label: string) => {
      const normalizedLabel = label.toLowerCase();

      if (normalizedLabel === 'deviation detected') {
        // Check if it is for annotations or embeddedImages
        if (difference.embeddedImageDetails) {
          const deletionTarget = DifferenceUtils.getEmbeddedImageDeletionTarget(difference);
          if (deletionTarget === 'source') {
            return 'No match was found in the Source file';
          }
          if (deletionTarget === 'target') {
            return 'No match was found in the New file';
          }
          return 'No match was found';
        }
        return 'No annotation match was found in your New document';
      }

      switch (normalizedLabel) {
        case 'major changes':
          return 'Many changes detected—needs review';
        case 'some changes':
          return 'Some changes detected—check carefully';
        case 'likely match':
          return 'Minimal or no changes found—likely match';
        case 'as intended':
          return 'The annotation was successfully applied to your New document';
        case 'misapplied':
          return 'The annotation was misapplied to your New document';
        case 'pass':
          return 'Check passed';
        case 'fail':
          return 'Check failed';
        case 'merged':
          return 'Click to unmerge';
        case 'delete':
          return 'Click to delete';
        case 're-inspected':
          return 'New re-inspected\nresult to review';
        default:
          return '';
      }
    };

    if (isEmbeddedImage) {
      if (isUserLinkedEmbeddedImage) {
        return {
          label: 'LIKELY MATCH',
          color: 'Green',
          hoverLabel: 'Undo Match',
          tooltip: getTooltipText('LIKELY MATCH'),
          actionable: true,
          onClick: handleUnlinkEmbeddedImage,
          icon: (
            <LinkedImageIcon
              sx={{
                fontSize: '14px',
                top: 0,
                left: 0,
                position: 'relative',
                color: '#2ED2AB',
              }}
            />
          ),
          hoverIcon: <UndoIcon />,
          testId: 'embedded_image_unlink_tag',
        };
      }
      const { label, color } = getConfidenceLevel(confidenceLevel);
      return { label, color, tooltip: getTooltipText(label) };
    } else if (isIntendedStatus) {
      const getColor = () => {
        return isIntendedStatus.toLowerCase() === 'as intended' ? 'Green' : 'Orange';
      };
      return {
        label: isIntendedStatus,
        color: getColor(),
        tooltip: getTooltipText(isIntendedStatus),
      };
    } else if (isUnmatchedAnnotation) {
      return { label: 'DEVIATION DETECTED', color: 'Grey', tooltip: getTooltipText('DEVIATION DETECTED') };
    } else if (isFontMetrics) {
      const label = difference.fontMetricsDetails.contextTag;
      const getColor = () => {
        return label.toLowerCase() === 'pass' ? 'Green' : 'Red';
      };
      return { label: label, color: getColor(), tooltip: getTooltipText(label) };
    } else if (isMerged) {
      return {
        label: 'MERGED',
        color: 'white',
        tooltip: getTooltipText('MERGED'),
        actionable: true,
        onClick: handleUnmerge,
        icon: <MergeIcon />,
        hoverIcon: <UndoIcon />,
      };
    } else if (isMarkup) {
      return {
        label: 'DELETE',
        color: 'white',
        tooltip: getTooltipText('DELETE'),
        actionable: true,
        onClick: handleRemoveMarkup,
        icon: <DeleteIcon sx={{ fontSize: '14px' }} />,
        testId: 'markup_context_tag',
      };
    } else if (isResynced) {
      return {
        label: 'RE-INSPECTED',
        color: 'white',
        tooltip: getTooltipText('RE-INSPECTED'),
        icon: <ResyncIcon sx={{ width: '12px', height: '12px', opacity: 0.6 }} />,
        testId: 'resyncContextBadge',
      };
    }
    return null;
  };

  useEffect(() => {
    if (loadingRestoreAllDifferences) {
      setIsLoading(true);
      // it isn't necessary to disable loading once finished loading because component should be unmounted when request is finished, so only disable loading if theres an error
    } else if (loadingRestoreAllError || updateDifferenceError || patchGroupError) {
      setIsLoading(false);
    }
  }, [loadingRestoreAllDifferences, loadingRestoreAllError, patchGroupError, updateDifferenceError]);

  const getStatusId = useCallback(
    (statusType: string) => {
      if (statuses?.currentStatuses) {
        const statusIdEntry = Object.entries(statuses.currentStatuses).find(
          ([_, status]) => (status as any).statusType === statusType,
        );
        return statusIdEntry ? statusIdEntry[0] : '';
      }
      return '';
    },
    [statuses?.currentStatuses],
  );

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (!isSelected) return;

      const isCtrlOrCmd = event.ctrlKey || event.metaKey;

      if (isCtrlOrCmd && event.key === 'm') {
        handleToggleComment(event, !isCommentOpen);
        event.preventDefault();
      }

      if (event.key === 'F1' || event.key === 'F2' || event.key === 'F3') {
        event.preventDefault();
        event.stopPropagation();

        // Prevent repeated hotkey presses during auto-advance window
        if (hotkeyLockRef.current) {
          return;
        }

        const statusId = getStatusId(statusHotkeyMap[event.key as keyof typeof statusHotkeyMap]);
        if (statusId) {
          if (difference.groupId) {
            dispatch(patchGroup(inspectionId, difference.groupId, { statusId }));
          } else {
            dispatch(updateDifference(difference.id, { statusId }));
          }
        }

        // Lock further hotkey handling until the next selection occurs
        hotkeyLockRef.current = true;
        // Safety unlock in case navigation fails to reselect (slightly longer than navigation debounce)
        window.setTimeout(() => {
          hotkeyLockRef.current = false;
        }, 500);

        // Ensure the next item is scrolled into view before clicking (to guarantee render)
        const nextIndex = props.numberOfDifference + 1;
        // Use shorter delay for hotkey auto-advance, without duplicating handlers
        window.setTimeout(() => {
          props.handleOnStopKeyboardNavigation(nextIndex);
        }, 50);
      }
    }

    if (flags?.enableKeyboardNavigation) {
      window.addEventListener('keydown', handleKeyDown, { capture: true });

      // Also listen inside PDF viewer iframes so keyboard shortcuts work when viewer is focused
      const sourceWin = PDFTronManager.getIframeWindow(DocumentTypes.source);
      const targetWin = PDFTronManager.getIframeWindow(DocumentTypes.target);
      sourceWin?.addEventListener('keydown', handleKeyDown, { capture: true });
      if (targetWin && targetWin !== sourceWin) {
        targetWin.addEventListener('keydown', handleKeyDown, { capture: true });
      }

      return () => {
        window.removeEventListener('keydown', handleKeyDown, { capture: true });
        if (sourceWin) sourceWin.removeEventListener('keydown', handleKeyDown, { capture: true });
        if (targetWin && targetWin !== sourceWin)
          targetWin.removeEventListener('keydown', handleKeyDown, { capture: true });
      };
    }

    return undefined;
  }, [
    isSelected,
    isCommentOpen,
    handleToggleComment,
    getStatusId,
    flags?.enableKeyboardNavigation,
    difference.id,
    difference.groupId,
    inspectionId,
    props.handleOnStopKeyboardNavigation,
    props.numberOfDifference,
    dispatch,
  ]);

  function handleSelectDifference(e: React.MouseEvent) {
    e.stopPropagation();
    selectDifference();
  }

  function handleSuggestWord(e: React.MouseEvent) {
    e.stopPropagation();
    if (!canSuggestToCustomDictionary || hasBeenSuggested) return;

    const suggestWord = {
      suggestedWord: difference.source.text,
      suggestedDictionaryId: customDictionaryId || null,
    };
    suggestWordMutation.mutate(suggestWord);

    if ('smartDiscard' in difference && difference.smartDiscard) {
      tracker.track({
        name: `smart-discard-added-to-dictionary`,
        data: {
          differenceType: difference.type,
          differenceWord: difference.source.text,
        },
      });
    }
  }
  function handleMarkAsFalsePositive(e: React.MouseEvent) {
    e.stopPropagation();

    const newComment = isFalsePositiveComment(difference?.comment) ? '' : FALSE_POSITIVE_COMMENT;

    // Prefill the comment box, but do NOT persist until the user clicks Save.
    dispatch(inspection.actions.setPersistedComment({ diffIds: [difference.id], comment: newComment }));

    handleToggleComment(e, true);
  }

  const debouncedSuggestWord = useCallback(debounce(handleSuggestWord, 300), []);

  const handleCommentButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (isCommentOpen) {
        handleToggleComment(e, false);
        return;
      }

      // Just trigger an update to mark as viewed in the backend
      if (difference.groupId) {
        dispatch(patchGroup(inspectionId, difference.groupId, { viewed: true }));
      } else {
        dispatch(updateDifference(difference.id, { viewed: true }));
      }

      handleToggleComment(e, true);
    },
    [difference.groupId, difference.id, inspectionId, isCommentOpen, dispatch, handleToggleComment],
  );

  const DescriptionComponent =
    difference.type === DifferenceTypeNames.ReadingOrder && !flags.readingOrderUxV2
      ? undefined
      : descriptionComponents[difference.type as DifferenceTypeNames];
  let diffDesc;
  // TEMP: keep this condition here until we move logic from here to difference type components
  if (difference.type === DifferenceTypeNames.Graphics) {
    // Check if this is a merged difference
    if (isMergedDifference(difference)) {
      diffDesc = (
        <MergedGraphicDifference
          difference={difference}
          classes={{ classes }}
          isSelected={isSelected}
          differenceNumber={numberOfDifference}
          multipleSelectionMode={multipleSelectionMode}
        />
      );
    } else {
      diffDesc = (
        <GraphicDifference
          difference={difference}
          classes={{ classes }}
          isSelected={isSelected}
          differenceNumber={numberOfDifference}
          multipleSelectionMode={multipleSelectionMode}
        />
      );
    }
  } else if (DescriptionComponent) {
    diffDesc = (
      <DescriptionComponent
        classes={{ classes }}
        difference={difference}
        isSelected={isSelected}
        differenceNumber={numberOfDifference}
        multipleSelectionMode={multipleSelectionMode}
      />
    );
  } else {
    diffDesc = (
      <DifferenceDescription
        difference={difference}
        isBold={!difference.viewed || isSelected}
        multipleSelectionMode={multipleSelectionMode}
      />
    );
  }

  const getCardClassName = () => {
    const isInMultipleSelection = multipleSelectionMode && !isCheckboxSelected;

    return cx(
      classes.root,
      // Multiple selection mode
      multipleSelectionMode && isCheckboxSelected && classes.multipleSelectionChecked,
      isInMultipleSelection && isSelected && classes.selectedMultiple,
      !isCheckboxSelected && !isSelected && !difference.viewed && classes.backgroundColorEnabled,
      !isCheckboxSelected && !isSelected && difference.viewed && classes.backgroundColorRead,

      // Normal mode
      !multipleSelectionMode && isSelected && classes.selectedMultiple,

      // Other states
      isLoading && classes.loading,
      !multipleSelectionMode && !!isKeyboardFocused && !isSelected && classes.keyboardFocused,

      // Hover states
      !isCheckboxSelected && classes.hover,
      isCheckboxSelected && classes.checkedHover,
    );
  };
  const getCardHighlightColor = (differenceType: DifferenceTypeNames, inspectionHighlightColor: ColorId) => {
    // For single-color text highlight selections, ALL difference cards should have a red border
    if (inspectionHighlightColor !== ColorId.MULTICOLOR) {
      return DEFAULT_DIFFERENCE_COLOR; // Always use red for single-color selections
    }

    // For multicolor selection, use the difference type specific color if supported
    if (DifferenceTypesWithHighlightColor.includes(differenceType)) {
      return differenceHighlightColors[differenceType] || DEFAULT_DIFFERENCE_COLOR;
    }
    return DEFAULT_DIFFERENCE_COLOR;
  };

  const cardBorderLeftColor = isSelected ? getCardHighlightColor(difference.type, highlightColor) : 'rgba(0,0,0,0)';

  const contextTagProps = useContextTag ? calculateTagProperties() : null;
  const showRangeLabel = groupIndex != null && props.groupLength > 1;
  // A four-digit range label plus a full-width tag would run into the card's title, so past that
  // point the tag drops its text and keeps only the icon (its meaning stays in the tooltip).
  const collapseContextTag = showRangeLabel && props.groupLength > 999;

  return (
    <Grid
      ref={elementRef}
      container
      direction="column"
      className={getCardClassName()}
      onClick={handleSelectDifference}
      data-testid={`difference_card_${1 + props.numberOfDifference}`}
      id={difference.groupId ? `${difference.groupId}-group-element` : `${difference.id}-difference-element`}
      style={{ borderLeftColor: cardBorderLeftColor }}
    >
      {multipleSelectionMode && (
        <div className={classes.checkboxContainer} onClick={(e) => e.stopPropagation()}>
          <MultipleSelectionCheckbox
            checked={isCheckboxSelected}
            onChange={(checked) => {
              if (onCheckboxChange) {
                onCheckboxChange(difference, checked);
              }
            }}
            aria-label={`Select difference ${numberOfDifference + 1}`}
            variant="card"
          />
        </div>
      )}

      {(showRangeLabel || contextTagProps) && (
        <div className={classes.cardMeta}>
          {showRangeLabel && (
            <span
              className={classes.rangeLabel}
              data-testid={`difference_card_range_label_${groupIndex}`}
            >{`#${groupIndex}.1\u2013${groupIndex}.${props.groupLength}`}</span>
          )}
          {contextTagProps && <ContextTag {...contextTagProps} iconOnly={collapseContextTag} />}
        </div>
      )}

      <Grid
        container
        direction="column"
        style={{
          paddingLeft: multipleSelectionMode ? '28px' : '0px',
          paddingRight: '8px',
          transition: 'padding-left 300ms ease-out',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Grid item container alignItems="center" className={classes.transition}>
          <Grid
            item
            className={`${classes.indicatorGrid} ${multipleSelectionMode ? classes.indicatorWithCheckbox : ''}`}
            id={`${difference.groupId || difference.id}-${difference.viewed ? 'read' : 'unread'}`}
            data-testid={`difference_card_read_indicator_${1 + props.numberOfDifference}`}
          >
            <Lens
              className={`${classes.indicator} ${difference.viewed ? classes.indicatorRead : classes.indicatorUnread}`}
            />
          </Grid>
          <Grid item container className={classes.title}>
            <Grid item>
              <Typography
                data-testid={`difference_card_number_${1 + props.numberOfDifference}`}
                variant="button"
                component="span"
                className={`${classes.number} ${classes.differenceType} ${classes.rightMargin} ${
                  isSelected ? classes.numberSelected : ''
                }`}
              >
                {`${numberOfDifference + 1}.`}
              </Typography>
            </Grid>
            <Grid item>
              <Typography
                data-testid={`difference_card_display_name_${1 + props.numberOfDifference}`}
                variant="button"
                component="span"
                className={classes.differenceType}
                style={{
                  fontWeight: !difference.viewed || isSelected ? 800 : 600,
                }}
              >
                {difference.displayName}
              </Typography>
            </Grid>
            {props.groupLength !== 0 && (
              <Grid item data-testid={`difference_card_group_chip_parent_${1 + props.numberOfDifference}`}>
                <GroupChip number={props.groupLength} groupId={difference.groupId} />
              </Grid>
            )}
            {difference.smartDiscard && (
              <Grid item>
                <GVTooltip
                  title="This result was originally discarded because it's likely a valid word, name, brand, or abbreviation."
                  side="bottom"
                  className={styles.tooltip}
                >
                  <span>
                    <SmartDiscardIcon className={classes.smartDiscardIcon} />
                  </span>
                </GVTooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
        {/* Difference description */}
        <Grid
          data-testid={`difference_card_description_${1 + props.numberOfDifference}`}
          className={`${isLoading && classes.loadingOpacity}`}
          style={{ display: 'contents' }}
        >
          {diffDesc}
        </Grid>
        <Grid item className={`${classes.rightMargin} ${classes.iconContainer}`}>
          <Grid item display={'inline-flex'} className={`${classes.customActionsButtons}`}>
            {difference.statusId ? (
              <CustomStatusButton
                status={statuses?.currentStatuses[difference.statusId] || statuses?.allStatuses[difference.statusId]}
                handleSelectStatus={(e) => {
                  e.stopPropagation();
                  handleSelectStatus(null);
                }}
                className={cx(isLoading && classes.loading && classes.disabledButton)}
                selected={true}
              />
            ) : (
              Object.values(statuses?.currentStatuses ?? []).map(
                (status: any, index: number) =>
                  status.active && (
                    <CustomStatusButton
                      key={index}
                      status={status}
                      handleSelectStatus={(e) => {
                        e.stopPropagation();
                        handleSelectStatus(status.id);
                      }}
                      className={cx(isLoading && classes.loading && classes.disabledButton)}
                      selected={false}
                    />
                  ),
              )
            )}
          </Grid>

          <Grid item className={classes.rightActions}>
            {flags.flagFalsePositivesEnabled && (
              <GVIconButton
                testId={`difference_card_mark_false_positive_${1 + props.numberOfDifference}`}
                color={falsePositive ? 'secondary' : 'primary'}
                size="small"
                onClick={handleMarkAsFalsePositive}
                id={`${difference.groupId || difference.id}-false-positive-toggle`}
                className={cx(isLoading && classes.loading && classes.disabledButton)}
                icon={
                  <GVTooltip title="Report false positive" side="bottom">
                    <OutlinedFlagIcon className={classes.icons}></OutlinedFlagIcon>
                  </GVTooltip>
                }
              />
            )}
            {isSpellingDiff && (
              <GVTooltip title={suggestedWordTooltip} side="bottom">
                <span>
                  <GVIconButton
                    testId={`difference_card_suggest_word_button_${1 + props.numberOfDifference}`}
                    color="primary"
                    className={cx(isLoading && classes.loading && classes.disabledButton)}
                    size="small"
                    onClick={(e: React.MouseEvent) => {
                      debouncedSuggestWord(e);
                    }}
                    disableRipple={!!hasBeenSuggested || !canSuggestToCustomDictionary}
                    disabled={!canSuggestToCustomDictionary}
                    id={`${difference.groupId || difference.id}-toggle-comments`}
                    icon={
                      hasBeenSuggested ? (
                        <AccessAlarmsIcon className={classes.icons && classes.accessAlarm} />
                      ) : (
                        <AddCircleIcon className={classes.icons && classes.suggestWordIcon} />
                      )
                    }
                  />
                </span>
              </GVTooltip>
            )}
            <GVTooltip title="Comment" side="bottom">
              <GVIconButton
                testId={`difference_card_comment_button_${1 + props.numberOfDifference}`}
                color="primary"
                className={`${isLoading && classes.loading && classes.disabledButton}`}
                size="small"
                style={{ marginLeft: '2px' }}
                onClick={handleCommentButtonClick}
                id={`${difference.groupId || difference.id}-toggle-comments`}
                icon={
                  difference?.comment ? (
                    <CommentsIcon className={classes.icons} />
                  ) : (
                    <NoCommentsIcon className={classes.icons} />
                  )
                }
              />
            </GVTooltip>
          </Grid>
        </Grid>
        {/* Comments */}
        {isCommentOpen && (
          <Grid
            item
            className={classes.commentContainer}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
          >
            <DifferenceComments
              numberOfDifference={numberOfDifference}
              difference={difference}
              handleToggleComment={handleToggleComment}
              onSaveComment={handleOnSaveComment}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default DifferenceElementView;
